/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { LoadingButton } from '@mui/lab'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import OrbitSession from 'hooks/useCookies'

interface IUploadTerminatedCSVProps {
  refetchAllUsers: () => void
  refetchTerminatedUserStats: () => void
}

export default function UploadTerminatedCSV({
  refetchAllUsers,
  refetchTerminatedUserStats
}: IUploadTerminatedCSVProps) {
  const [uploadErr, setUploadErr] = useState<string>('')
  const [uploadStatus, setUploadStatus] = useState<string>('')

  const [uploadedFilename, setUploadedFilename] = useState<string>('')
  const [uploadedObject, setUploadedObject] = useState<any>(null)
  const faberToken = OrbitSession.get('faberToken')

  /**Function to check csv format uploaded */
  function checkCSV(csvFile) {
    const csvFileRows: string[] = csvFile.split('\n')
    const formattedRows = csvFileRows.map((row) =>
      row.replaceAll('"', '').trim().split(',')
    )
    const formattedCSVRows = formattedRows.flat()

    let err = false

    if (formattedCSVRows[0] !== 'siNumber') {
      err = true
    }
    if (err) {
      setUploadStatus('ERROR')
      setUploadErr('Invalid csv format, please check your column headers')
      return
    }

    setUploadStatus('PENDING')
  }

  const resetAllStates = () => {
    setUploadedFilename('')
    setUploadedObject(null)
    setUploadErr('')
    setUploadStatus('')
  }

  const {
    mutate: uploadTerminatedSins,
    isLoading: isUploadedTerminatedSinsLoading
  } = useMutation(SubscriptionsAPI.uploadTerminatedSins, {
    onSuccess: () => {
      setUploadStatus('SUCCESS')
      toast.success('Successfully uploaded CSV')
      refetchAllUsers()
      refetchTerminatedUserStats()
    },
    onError: () => {
      toast.error('There is a problem trying to upload your file to the server')
    },
    onSettled: () => {
      setTimeout(resetAllStates, 3000)
    }
  })
  useEffect(() => {
    if (uploadStatus === 'PENDING') {
      const formData = new FormData()
      formData.append('file', uploadedObject)

      uploadTerminatedSins({
        faberToken: faberToken,
        uploadObject: formData
      })
    }
  }, [uploadStatus, uploadedObject])

  const onFileUpload = async (file) => {
    const fileName = file.name
    const reader = new FileReader()

    reader.onload = async (e) => {
      const text = e.target?.result
      const output = checkCSV(text)
      if (output === null) {
        return null
      } else {
        setUploadedObject(file)
      }
    }

    reader.readAsText(file)
    setUploadedFilename(fileName)
  }

  return (
    <>
      <Stack alignItems='flex-start'>
        <LoadingButton
          variant='outlined'
          component='label'
          sx={{ fontSize: '12px', p: 3, background: 'white' }}
          startIcon={<FileUploadIcon />}
          loading={isUploadedTerminatedSinsLoading}
          loadingIndicator={<CircularProgress />}
        >
          Upload file for terminated users
          <input
            type='file'
            hidden
            accept='.csv'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!e.target.files) return
              onFileUpload(e.target.files[0])
            }}
          />
        </LoadingButton>
        {uploadedFilename && (
          <Typography sx={{ width: '80%' }}>
            Uploaded file:
            <br /> <strong>{uploadedFilename}</strong>
          </Typography>
        )}
        <Typography variant='subtitle1' sx={{ color: 'red' }}>
          {uploadStatus === 'ERROR' && uploadErr}
        </Typography>
      </Stack>
    </>
  )
}
